class ExpenseService {
  // Helper to format currency
  formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    return parseFloat(value).toFixed(2);
  }

  // Calculate total expenses
  calculateTotalExpenses(expenses, startDate, endDate) {
    if (!expenses || !Array.isArray(expenses)) {
      return {
        total: 0,
        byCategory: {},
        byTerm: {},
        byVendor: {},
        count: 0
      };
    }

    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date || expense.createdAt || Date.now());
      return expenseDate >= startDate && expenseDate <= endDate;
    });

    let total = 0;
    const byCategory = {};
    const byTerm = {};
    const byVendor = {};

    // Valid purpose categories from expenses page (Add Expenses dialog)
    const validPurposes = [
      'Food', 'Building', 'Transport', 'Health', 'Accident', 
      'Books', 'Academics', 'Computer', 'Library', 'Canteen', 
      'Classroom', 'Office', 'Washroom'
    ];

    filteredExpenses.forEach(expense => {
      const amount = parseFloat(expense.amountspent || expense.amount) || 0;
      total += amount;

      // Use the actual purpose from expense record
      // If purpose is one of the valid categories, use it directly
      // Otherwise, use 'Other'
      const purpose = expense.purpose || expense.description || 'Other';
      const category = validPurposes.includes(purpose) ? purpose : 'Other';

      if (!byCategory[category]) {
        byCategory[category] = 0;
      }
      byCategory[category] += amount;

      // Track by term if available
      if (expense.term) {
        if (!byTerm[expense.term]) {
          byTerm[expense.term] = 0;
        }
        byTerm[expense.term] += amount;
      }

      // Track by vendor/recipient
      const vendor = expense.paidto || expense.vendor || 'Unknown';
      if (!byVendor[vendor]) {
        byVendor[vendor] = 0;
      }
      byVendor[vendor] += amount;
    });

    return {
      total,
      byCategory,
      byTerm,
      byVendor,
      count: filteredExpenses.length
    };
  }

  // Calculate salaries expense (if salaries are stored separately)
  calculateSalariesExpense(salaries, startDate, endDate) {
    if (!salaries || !Array.isArray(salaries)) {
      return {
        total: 0,
        byPosition: {},
        byMonth: {},
        count: 0
      };
    }

    const filteredSalaries = salaries.filter(salary => {
      const salaryDate = new Date(salary.date || salary.month || salary.createdAt || Date.now());
      return salaryDate >= startDate && salaryDate <= endDate;
    });

    let total = 0;
    const byPosition = {};
    const byMonth = {};

    filteredSalaries.forEach(salary => {
      const amount = parseFloat(salary.amount || salary.amountpaid) || 0;
      total += amount;

      // Track by position
      const position = salary.position || salary.positiondescription || 'Unknown';
      if (!byPosition[position]) {
        byPosition[position] = 0;
      }
      byPosition[position] += amount;

      // Track by month
      const month = salary.month || new Date(salary.date || Date.now()).toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!byMonth[month]) {
        byMonth[month] = 0;
      }
      byMonth[month] += amount;
    });

    return {
      total,
      byPosition,
      byMonth,
      count: filteredSalaries.length
    };
  }

  // Get expense breakdown for reporting
  getExpenseBreakdown(expenses, salaries, startDate, endDate) {
    const generalExpenses = this.calculateTotalExpenses(expenses, startDate, endDate);
    const salaryExpenses = this.calculateSalariesExpense(salaries, startDate, endDate);

    // Merge salary expenses into general expenses
    if (salaryExpenses.total > 0) {
      if (!generalExpenses.byCategory['Salaries & Wages']) {
        generalExpenses.byCategory['Salaries & Wages'] = 0;
      }
      generalExpenses.byCategory['Salaries & Wages'] += salaryExpenses.total;
      generalExpenses.total += salaryExpenses.total;
    }

    return {
      total: generalExpenses.total,
      byCategory: generalExpenses.byCategory,
      byTerm: generalExpenses.byTerm,
      byVendor: generalExpenses.byVendor,
      salaries: salaryExpenses,
      count: generalExpenses.count + salaryExpenses.count
    };
  }
}

export default new ExpenseService();
